import React, { useContext, useEffect, useMemo } from "react";
import { Panel, Skeleton } from "components/commons";
import lang from "translations";
import { StaffContext } from "contexts";
import { useApi, useRouter } from "hooks";
import { Path } from "paths";
import { searchLocationWithTransaction } from "apis";
import { formatNumberToMoneyCurrency, getCurrencySymbol } from "services/money.service";
import { parseAmountToNumber } from "services";
import styles from "../dashboard-charts.module.scss";

const LOCATION_COLORS = ["#008D91", "#2C9DA0", "#7FC3C5", "#EE99AE"];

const LocationsSalesCard = ({ mappedData = [], submittedParams = {}, loading, error, retry }) => {
  const { staff } = useContext(StaffContext);
  const { history } = useRouter();
  const canViewReport = !(staff?.roles?.includes("FRONT_OFFICE") && staff?.roles?.length === 1);
  const {
    request: searchLocationRequest,
    mappedData: locationOptions,
    loading: loadingLocations,
  } = useApi({
    api: searchLocationWithTransaction,
    mapper: {
      value: {
        key: "locationId",
      },
      text: {
        key: "locationName",
      },
    },
    isArray: true,
    handleOwnError: true,
  });

  useEffect(() => {
    if (submittedParams.venueId) {
      searchLocationRequest({
        venueId: submittedParams.venueId,
        page: 1,
        pageSize: 100,
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedParams.venueId]);

  const locations = useMemo(() => {
    const salesByLocation = new Map(
      mappedData.map((location) => [
        location.name,
        parseAmountToNumber(location.value) || 0,
      ]),
    );
    const selectedLocationIds = (submittedParams.locationIds || []).map(String);
    const availableLocations = selectedLocationIds.length
      ? locationOptions.filter((location) => selectedLocationIds.includes(String(location.value)))
      : locationOptions;
    const sourceLocations = locationOptions.length
      ? availableLocations.map((location) => ({
        name: location.text,
        value: salesByLocation.get(location.text) || 0,
      }))
      : mappedData.map((location) => ({
        name: location.name,
        value: parseAmountToNumber(location.value) || 0,
      }));
    const sortedLocations = sourceLocations.sort((a, b) => b.value - a.value);
    const maxValue = Math.max(...sortedLocations.map((location) => location.value), 0);

    return sortedLocations.map((location, index) => ({
      ...location,
      color: LOCATION_COLORS[index] || LOCATION_COLORS[LOCATION_COLORS.length - 1],
      width: maxValue && location.value > 0 ? Math.max((location.value / maxValue) * 100, 4) : 0,
    }));
  }, [locationOptions, mappedData, submittedParams.locationIds]);

  const hasLocations = locations.length > 0;

  return (
    <Panel className={styles.locationsSalesCard} mb="0" parent={false}>
      <div className={styles.locationsSalesHeader}>{lang.topLocations}</div>

      {(loading || loadingLocations) && <Skeleton />}

      {!loading && !loadingLocations && error && (
        <div className={styles.salesOverTimeMessage} onClick={retry}>
          {lang.failedToLoad} <span>{lang.clickHereToRetry}</span>
        </div>
      )}

      {!loading && !loadingLocations && !error && !hasLocations && (
        <div className={styles.salesOverTimeMessage}>{lang.noDataAvailable}</div>
      )}

      {!loading && !loadingLocations && !error && hasLocations && (
        <>
          <div className={styles.locationsSalesList}>
            {locations.map((location) => (
              <div key={location.name}>
                <div className={styles.locationsSalesHead}>
                  <span className={styles.locationsSalesName}>{location.name}</span>
                  <span className={styles.locationsSalesAmount}>
                    <span className={styles.locationsSalesCurrency}>{getCurrencySymbol()}</span>
                    {formatNumberToMoneyCurrency(location.value)}
                  </span>
                </div>
                <div className={styles.locationsSalesTrack}>
                  <div
                    className={styles.locationsSalesFill}
                    style={{
                      width: `${location.width}%`,
                      background: location.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {canViewReport && (
            <button
              type="button"
              className={styles.locationsSalesLink}
              onClick={() => history.push(Path.REPORT_SALES)}
            >
              {"View sales report \u2192"}
            </button>
          )}
        </>
      )}
    </Panel>
  );
};

export default LocationsSalesCard;
